// src/features/banquetOwnerPanel/components/GenerateOwnerLinkButton.jsx
//
// Super-admin-only control that turns a banquet sitting in "pending_owner_fill" into a
// shareable /owner-fill/:token link — used both right after Quick Add
// (SuperAdminQuickAddBanquetPage.jsx's success screen) and later from the "Pending Owner
// Fill" list (SuperAdminBanquetOwnersPage.jsx), so a lost or unsent link is always
// recoverable. Clicking again always regenerates (the Cloud Function invalidates the old
// token), matching the "Regenerate" framing where this is reused for that purpose.

import React, { useState } from "react";
import { FaCheck, FaLink } from "react-icons/fa6";
import { generateOwnerFillLink } from "../services/banquetOwnerBanquetService.js";

export default function GenerateOwnerLinkButton({ banquetId, label = "Generate Owner Link" }) {
  const [status, setStatus] = useState("idle"); // idle | generating | success | error
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setStatus("generating");
    setError("");
    setCopied(false);
    try {
      const response = await generateOwnerFillLink({ banquetId });
      setLink(`${window.location.origin}/owner-fill/${response.token}`);
      setStatus("success");
    } catch (generateError) {
      setStatus("error");
      setError(generateError.message || "Unable to generate the link. Please try again.");
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch (copyError) {
      setError("Couldn't copy automatically — select and copy the link manually.");
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleGenerate}
        disabled={status === "generating"}
        className="inline-flex min-h-[38px] items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <FaLink /> {status === "generating" ? "Generating..." : link ? "Regenerate Link" : label}
      </button>
      {error ? <p className="mt-1.5 max-w-[280px] text-xs font-semibold text-rose-700">{error}</p> : null}
      {status === "success" && link ? (
        <div className="mt-2 flex max-w-[320px] flex-wrap items-center gap-2">
          <input
            readOnly
            value={link}
            onFocus={(event) => event.target.select()}
            className="min-w-[200px] flex-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex min-h-[30px] items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-bold text-emerald-700"
          >
            {copied ? <><FaCheck /> Copied!</> : "Copy Link"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
